
// Base.
import Component from 'vue-class-component'
import Vue from 'vue'
import { Watch } from 'vue-property-decorator'
import { State } from 'vuex-class'
import { User } from '../store/index'
// Used Models.
import UserMIREO from '../models/userMireo'
import GridHeader from '../models/gridHeader'

// Used Components.
import AssignService from '../components/Users/AssignService.vue'
import CardTable from '../components/Layout/CardTable.vue'
import ResetSinglePassword from '../components/Users/ResetSinglePassword.vue'
import StartMission from '../components/UsersMireo/StartMission.vue'
import EndMission from '../components/UsersMireo/EndMission.vue'
import CardTitle from '../components/Atomic/CardTitle.vue'
import Permissions from '../models/constants/Permissions'
import RedButton from '@/components/Atomic/RedButton.vue'
import HelpTooltip from '@/components/Atomic/HelpTooltip.vue'

import Establishment from '@/models/establishment'
import SearchField from '@/components/Atomic/SearchField.vue'

// Used for notifications.
import { INotificationService } from '../services/NotificationService'
import UserMireoService from '@/services/UserMireoService'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import DatePickerActions from '@/components/Atomic/DatePickerActions.vue'
import BlockButton from '@/components/Atomic/BlockButton.vue'
import ChangeEmail from '@/components/ChangeEmail.vue'

@Component({
  components: {
    BlockButton,
    CardTable,
    DatePickerActions,
    AssignService,
    ResetSinglePassword,
    StartMission,
    EndMission,
    CardTitle,
    SearchField,
    RedButton,
    HelpTooltip,
    ChangeEmail,
  },
})
export default class UsersMireo extends Vue {
  public $refs!: {
    startMissionDialog: StartMission,
    endMissionDialog: EndMission,
    datePickerDialog: DatePickerActions,
  }
  @State private user!: User

  // User properties.
  private users: UserMIREO[] = []
  private filteredUsers: UserMIREO[] = []
  private selectedUsers: UserMIREO[] = []

  // UI properties.
  private searchFilter: string = ''
  private basicSearch: string = ''
  private headers: GridHeader[] = []
  private establishments: Establishment[] = []
  private usersSchools: Establishment[] = []
  private names: string[] = []
  private firstNames: string[] = []
  private userNames: string[] = []
  private emails: string[] = []
  private personnalEmails: string[] = []
  private missionEndDates: string[] = []

  private isPending: boolean = false
  private canSearch: boolean = true
  private menu: boolean = false
  private date: string = ''
  // Filter properties.
  private dialogFilters: boolean = false
  private changeEmailDialog: boolean = false
  private selectedEstablishmentId: string = ''
  private selectedSchoolIDFilter: string = ''
  private selectedSchoolName: string = ''
  private selectedName: string = ''
  private selectedFirstName: string = ''
  private selectedUsername: string = ''
  private selectedDate: string = ''
  private selectedEmail: string = ''
  private selectedPersonnalEmail: string = ''
  private changeUserEmail!: UserMIREO
  private selectedMissionStatus: string = ''
  // Used to handle errors
  private errorHandlerService: ErrorHandlerService

  // Used for notification.
  private $notificationService!: INotificationService

  constructor() {
    super()
    this.errorHandlerService = new ErrorHandlerService()
  }

  public mounted() {
    this.initialize()
  }

  private initialize() {
    this.establishments = Object.assign([], this.user.schools)
    this.createHeaders()
  }

  private capitalize(value: string) {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
  }

  private isMissionStarted(user: UserMIREO) {
    const date_validite = new Date(user.date_validite)
    if (
      user.etablissement_nom !== '' &&
      date_validite.setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0)
    ) {
      return true
    }
    return false
  }

  private get hasSearchFilter() {
    return this.searchFilter !== '' && this.searchFilter !== null
  }

  private get hasUsersToFilter() {
    return this.users.length > 0
  }

  private sortStringValues(
    array: any[],
    key?: string,
    caseSensitive: boolean = false,
  ) {
    array.sort((a, b) => {
      let aValue
      let bValue

      if (key === undefined) {
        aValue = a as string
        bValue = b as string
      } else {
        aValue = a[key] as string
        bValue = b[key] as string
      }

      if (!caseSensitive) {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue > bValue) {
        return 1
      }
      if (aValue < bValue) {
        return -1
      }
      return 0
    })
  }

  public get parseDate() {
    return this.formatDateToDatePicker(this.selectedDate, '-')
  }

  public set parseDate(date: any) {
    this.selectedDate = date
    this.filterLogical()
  }

  private formatDateToDatePicker(selectedDate: string, splitCharacter: string) {
    let formattedDate: string | null = null
    if (selectedDate) {
      const [year, month, day] = selectedDate.split(splitCharacter)
      formattedDate = `${day}.${month}.${year}`
    }
    return formattedDate
  }

  private allowedDates(val: string) {
    return this.missionEndDates.indexOf(val) !== -1
  }

  private get hasAccessToChangeEmail(): boolean {
    const allowed = [Permissions.ChangeMireoEmail]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private showChangeEmailDialog(user: UserMIREO) {
    this.changeUserEmail = user
    this.changeEmailDialog = true
  }

  private async saveMailChange(chosenEmail: string, username: string) {
    await UserMireoService.updateMireoMask(chosenEmail, username)
    if (this.searchFilter) {
      this.search(this.searchFilter)
    } else {
      this.onSelectedEstablishmentIdChanged(this.selectedEstablishmentId, '')
    }
  }

  private closeMailChangeDialog() {
    this.changeEmailDialog = false
  }

  private async search(filter: string) {
    this.isPending = true
    try {
      this.users = await UserMireoService.search(filter)
      for (const user of this.users) {
        this.populateData(user)
      }
      this.sortData()
      this.filterLogical()
    } catch (error: any) {
      if (error.response !== undefined) {
        this.errorHandlerService.handle(this, error.response.data.code, true)
      }
    } finally {
      this.isPending = false
    }
  }

  @Watch('searchFilter')
  private async onFilterChanged(searchFilter: string, oldsearchFilter: string) {
    if (searchFilter) {
      if (searchFilter.length > 4) {
        // Prevents sending request while typing
        await new Promise((resolve) => setTimeout(resolve, 600))
        if (!this.isPending && this.searchFilter === searchFilter) {
          this.search(searchFilter)
        }
      }
    }
    if (searchFilter === '') {
      this.clearDataTable()
    }
  }

  private async getMireoUsers(establishmentId: string) {
    this.isPending = true

    try {
      this.users = await UserMireoService.getMireoUsersFromEstablishment(
        establishmentId,
      )
      this.addCurrentSchoolData(this.users, establishmentId)
      this.filterLogical()
    } catch (error: any) {
      this.errorHandlerService.handle(this, error.response.data.code, true)
    } finally {
      this.isPending = false
    }
  }

  private addCurrentSchoolData(users: UserMIREO[], establishmentId: string) {
    const establishment = this.establishments.find(
      (e) => e.etablissement_id === establishmentId,
    )

    if (establishment) {
      this.selectedSchoolName = establishment.nom
      for (const user of users) {
        this.populateData(user)
      }
      this.sortData()
    }
  }

  private populateData(user: UserMIREO) {
    this.populateSchools(user)
    this.populateNames(user)
    this.populateFirstNames(user)
    this.populateUsernames(user)
    this.populateEmails(user)
    this.populatePersonnalEmails(user)
    this.populateMissionEndDates(user)
  }

  private sortData() {
    this.sortStringValues(this.names)
    this.sortStringValues(this.firstNames)
    this.sortStringValues(this.userNames)
    this.sortStringValues(this.emails)
    this.sortStringValues(this.personnalEmails)
  }

  private populateSchools(user: UserMIREO) {
    if (user.etablissement_nom) {
      const schoolObject = this.usersSchools.find(
        (school) => school.etablissement_id === user.etablissement_id,
      )
      if (!schoolObject) {
        const schoolToAdd = {
          etablissement_id: user.etablissement_id,
          nom: user.etablissement_nom,
          region: '',
        }
        this.usersSchools.push(schoolToAdd)
      }
    }
  }

  private populateNames(user: UserMIREO) {
    if (user.nom) {
      if (!this.names.includes(user.nom)) {
        this.names.push(user.nom)
      }
    }
  }

  private populateFirstNames(user: UserMIREO) {
    if (user.prenom) {
      if (!this.firstNames.includes(user.prenom)) {
        this.firstNames.push(user.prenom)
      }
    }
  }

  private populateUsernames(user: UserMIREO) {
    if (user.identifiant) {
      if (!this.userNames.includes(user.identifiant)) {
        this.userNames.push(user.identifiant)
      }
    }
  }

  private populateEmails(user: UserMIREO) {
    if (user.courriel) {
      if (!this.emails.includes(user.courriel)) {
        this.emails.push(user.courriel)
      }
    }
  }

  private populatePersonnalEmails(user: UserMIREO) {
    if (user.courriel_autre) {
      if (!this.personnalEmails.includes(user.courriel_autre)) {
        this.personnalEmails.push(user.courriel_autre)
      }
    }
  }

  private populateMissionEndDates(user: UserMIREO) {
    const expirationDate = user.date_validite.toString()
    if (!this.missionEndDates.includes(expirationDate)) {
      this.missionEndDates.push(expirationDate)
    }
  }

  private onSelectedEstablishmentIdChanged(
    establishmentId: string,
    oldEstablishmentId: string,
  ) {
    this.basicSearch = ''
    if (!establishmentId) {
      this.canSearch = true
      if (this.searchFilter) {
        this.filterLogical()
        return
      }
      this.clearDataTable()
    }
    if (establishmentId && !this.searchFilter) {
      this.clearDataTable()
      this.canSearch = false
      this.getMireoUsers(establishmentId)
      this.selectedEstablishmentId = establishmentId
    }
  }

  private filterLogical() {
    this.clearFiltersData()
    this.filteredUsers = this.users.filter((user: UserMIREO) => {
      if (
        this.selectedSchoolIDFilter &&
        !user.etablissement_id.includes(this.selectedSchoolIDFilter)
      ) {
        return false
      }
      if (
        this.selectedEstablishmentId &&
        !user.etablissement_id.includes(this.selectedEstablishmentId)
      ) {
        return false
      }
      if (this.selectedName && !user.nom.includes(this.selectedName)) {
        return false
      }
      if (
        this.selectedFirstName &&
        !user.prenom.includes(this.selectedFirstName)
      ) {
        return false
      }
      if (
        this.selectedUsername &&
        !user.identifiant.includes(this.selectedUsername)
      ) {
        return false
      }
      if (this.selectedEmail && !user.courriel.includes(this.selectedEmail)) {
        return false
      }
      if (
        this.selectedPersonnalEmail &&
        !user.courriel_autre.includes(this.selectedPersonnalEmail)
      ) {
        return false
      }
      if (this.selectedDate) {
        const dateToCompare = user.date_validite.toLocaleString().split('T')[0]
        if (dateToCompare.indexOf(this.selectedDate) === -1) {
          return false
        }
      }
      if (this.selectedMissionStatus === 'Aucun') {
        if (
          this.selectedMissionStatus &&
          new Date(user.date_validite) >=
            new Date(
              new Date().getFullYear(),
              new Date().getMonth(),
              new Date().getDay(),
            ) &&
            user.date_validite.toString() !== '2199-07-31' &&
          user.etablissement_id !== ''
        ) {
          return false
        }
      } else if (this.selectedMissionStatus === 'En cours') {
        if (
          this.selectedMissionStatus &&
          (new Date(user.date_validite) <
            new Date(
              new Date().getFullYear(),
              new Date().getMonth(),
              new Date().getDay(),
            ) ||
            user.date_validite.toString() === '2199-07-31' ||
            user.etablissement_id === '')
        ) {
          return false
        }
      }
      this.populateData(user)
      return true
    })
    this.sortData()
  }

  private clearSearch() {
    this.clearDataTable()
  }

  private clearDataTable() {
    this.clearFiltersSelection()
    this.clearFiltersData()
    this.selectedEstablishmentId = ''
    this.selectedSchoolName = ''
    this.users = []
    this.filteredUsers = []
  }

  private clearFiltersData() {
    this.selectedUsers = []
    this.names = []
    this.firstNames = []
    this.userNames = []
    this.usersSchools = []
    this.emails = []
    this.personnalEmails = []
    this.missionEndDates = []
  }

  private clearFiltersSelection() {
    this.selectedSchoolIDFilter = ''
    this.selectedName = ''
    this.selectedFirstName = ''
    this.selectedUsername = ''
    this.selectedPersonnalEmail = ''
    this.selectedEmail = ''
    this.selectedMissionStatus = ''
  }

  private resetFilterDialog() {
    this.clearFiltersSelection()
    this.filterLogical()
  }

  private canFilter(): boolean {
    return !!this.selectedSchoolName || !!this.searchFilter
  }

  private async resetPassword(user: UserMIREO) {
    this.isPending = true
    const username = user.identifiant
    const school = user.etablissement_id

    try {
      const response = await UserMireoService.resetUserPassword(user, school)
      this.$notificationService.success(
        this.$t('USER.ResetPasswordSucceedNotStudent').toString() +
          username +
          this.$t('USER.ResetPasswordSucceedNotStudent2').toString() +
          response['email'],
      )
    } catch (error: any) {
      this.errorHandlerService.handle(this, error.response.data.code, true)
    } finally {
      this.isPending = false
    }
  }

  private createHeaders() {
    this.headers.push(
      new GridHeader(
        this.$t('Establishment').toString(),
        'etablissements',
        'left',
        true,
      ),
    )
    this.headers.push(
      new GridHeader(this.$t('Name').toString(), 'nom', 'left', true),
    )
    this.headers.push(
      new GridHeader(this.$t('FirstName').toString(), 'prenom', 'left', true),
    )
    this.headers.push(
      new GridHeader(
        this.$t('Username').toString(),
        'identifiant',
        'left',
        true,
      ),
    )
    this.headers.push(
      new GridHeader(this.$t('Email').toString(), 'courriel', 'left', true),
    )
    this.headers.push(
      new GridHeader(
        this.$t('EmailPersonnal').toString(),
        'courriel_autre',
        'left',
        true,
      ),
    )
    this.headers.push(
      new GridHeader(
        this.$t('MIREOACCOUNT.EndDate').toString(),
        'date_validite',
        'left',
        true,
      ),
    )
    this.headers.push(
      new GridHeader(
        this.$t('MIREOACCOUNT.Actions').toString(),
        'actions',
        'left fixed',
        false,
        '5%',
        'fixed',
      ),
    )
  }

  private updateExpiryDate(account: UserMIREO) {
    this.$refs.datePickerDialog.show(account)
  }

  private async extendExpiryDate(event: DatePickerActions) {
    this.isPending = true
    try {
      await UserMireoService.extend(
        event.account.ad_personne_id,
        event.picker,
        event.account.identifiant,
        event.account.etablissement_id,
      )
      this.$notificationService.success(
        this.$t('MIREOACCOUNT.ValidityDateSuccess').toString(),
      )
    } catch (error: any) {
      this.errorHandlerService.handle(this, error.response.data.code, true)
    }
    this.handleDialogClose()
  }

  private startMission(account: UserMIREO) {
    this.$refs.startMissionDialog.show(account)
  }

  private endMission(account: UserMIREO) {
    this.$refs.endMissionDialog.show(account)
  }

  private handleDialogClose() {
    if (this.searchFilter !== '' && this.searchFilter !== null) {
      this.search(this.searchFilter)
    } else if (this.selectedSchoolName !== '') {
      this.onSelectedEstablishmentIdChanged(this.selectedEstablishmentId, '')
    }
  }

  private get hasAccessToManageMireoAccounts(): boolean {
    const allowed = [Permissions.ManageMireoAccounts]
    if (
      (this.user.permissions as []).some((permission) =>
        allowed.includes(permission),
      )
    ) {
      return true
    }
    return false
  }

  private get filterSelected() {
    const filters = [ 'selectedSchoolIDFilter', 'selectedName', 'selectedFirstName', 'selectedUsername', 'selectedEmail', 'selectedPersonnalEmail', 'selectedDate', 'selectedMissionStatus']
    return filters.filter( (filter) => this.$data[filter] !== '' && this.$data[filter] !== null)
  }

  private getShoolName(idSeneps) {
    return this.establishments.find((school) => school.etablissement_id === idSeneps)?.nom
  }

  private resetFilter(item) {
    this.$data[item] = ''
    this.filterLogical()
  }

}
